import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Card, Col, Row } from 'react-bootstrap';
import axiosInstance from '../../services/AxiosInstance';
import { hasInvoiceScannerSubscription } from '../../utils/invoiceScannerAccess';
import { getCreditBalance, openCreditTopUpModal, creditsWalletPath } from '../../services/CreditService';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import '../tasks/tasklist.css';
import '../users/userlist.css';

const LOW_BALANCE_THRESHOLD = 1;
const SNAP_BILL_HANDOFF_KEY = 'snap_bill_handoff';
const FALLBACK_PATH = '/task/tasklist';

function redirectToSnap(redirectUrl) {
  // Used only to detect a bfcache restore of this page after leaving for Snap.
  sessionStorage.setItem(SNAP_BILL_HANDOFF_KEY, '1');
  window.location.replace(redirectUrl);
}

export default function LaunchInvoiceScanner() {
  const navigate = useNavigate();
  const [state, setState] = useState('loading'); // loading | blocked | low_balance | redirecting
  const [balance, setBalance] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function launch() {
      try {
        const hasAccess = await hasInvoiceScannerSubscription();
        if (!hasAccess) {
          if (!cancelled) {
            navigate('/subscription-feature-required?module=invoice_scanner', { replace: true });
          }
          return;
        }

        const creditRes = await getCreditBalance();
        const totalBalance = Number(creditRes?.credits?.total_balance ?? 0);
        if (!cancelled) {
          setBalance(totalBalance);
        }

        if (totalBalance <= 0) {
          if (!cancelled) {
            setState('blocked');
            setMessage('Your credit balance is empty. Top up credits to use Snap Bill.');
          }
          return;
        }

        if (totalBalance < LOW_BALANCE_THRESHOLD) {
          if (!cancelled) {
            setState('low_balance');
            setMessage(`Your credit balance is low ($${totalBalance.toFixed(2)}). You can continue or top up first.`);
          }
          return;
        }

        if (!cancelled) {
          setState('redirecting');
        }

        const res = await axiosInstance.get('/auth/module-handoff/invoice_scanner/redirect-url');
        const redirectUrl = res.data?.redirectUrl;
        if (!redirectUrl) {
          throw new Error('Missing redirect URL');
        }
        if (!cancelled) {
          redirectToSnap(redirectUrl);
        }
      } catch (err) {
        console.error('Snap Bill launch failed:', err);
        sessionStorage.removeItem(SNAP_BILL_HANDOFF_KEY);
        if (!cancelled) {
          setState('blocked');
          setMessage(err.response?.data?.message || 'Unable to launch Snap Bill. Please try again.');
        }
      }
    }

    const onPageShow = (event) => {
      if (event.persisted && sessionStorage.getItem(SNAP_BILL_HANDOFF_KEY)) {
        sessionStorage.removeItem(SNAP_BILL_HANDOFF_KEY);
        navigate(FALLBACK_PATH, { replace: true });
      }
    };
    window.addEventListener('pageshow', onPageShow);

    // A leftover flag from a previous Snap visit must not block a fresh sidebar click.
    sessionStorage.removeItem(SNAP_BILL_HANDOFF_KEY);
    launch();
    return () => {
      cancelled = true;
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [navigate]);

  const proceedToScanner = async () => {
    setState('redirecting');
    try {
      const res = await axiosInstance.get('/auth/module-handoff/invoice_scanner/redirect-url');
      const redirectUrl = res.data?.redirectUrl;
      if (!redirectUrl) throw new Error('Missing redirect URL');
      redirectToSnap(redirectUrl);
    } catch (err) {
      sessionStorage.removeItem(SNAP_BILL_HANDOFF_KEY);
      setState('blocked');
      setMessage(err.response?.data?.message || 'Unable to launch Snap Bill.');
    }
  };

  if (state === 'loading' || state === 'redirecting') {
    return (
      <Row style={{ marginBottom: '0px !important' }}>
        <Col lg={12}>
          <Card className="user-list-card">
            <Card.Body style={{ minHeight: '400px', padding: '1.5rem' }}>
              <div className="loading-container">
                <div className="loading-spinner" />
                <p className="text-muted mb-0 mt-3">
                  {state === 'redirecting' ? 'Redirecting to Snap Bill…' : 'Checking access…'}
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <>
      <Row style={{ marginBottom: '0px !important' }}>
        <Col lg={12}>
          <Card className="user-list-card">
            <Card.Header style={{ display: 'block' }} className="border-0 bg-transparent">
              <h5 className="mb-1">Snap Bill</h5>
              <p className="text-muted small mb-0">Launch the Snap Bill module</p>
            </Card.Header>
            <Card.Body style={{ padding: '1.5rem' }}>
              <Alert variant={state === 'blocked' ? 'danger' : 'warning'}>
                {message}
                {balance != null && (
                  <div className="mt-2 small">Current balance: ${Number(balance).toFixed(2)}</div>
                )}
              </Alert>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="new-user-btn"
                  onClick={() => openCreditTopUpModal()}
                >
                  <i className="fas fa-plus me-2" aria-hidden="true" />
                  Top up credits
                </button>
                {state === 'low_balance' && (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={proceedToScanner}
                  >
                    Continue anyway
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(creditsWalletPath())}
                >
                  View wallet
                </button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <InternalFooter />
    </>
  );
}
